import { MemreFlexBox } from 'components/core';
import { MemreToggleButtonGroup } from 'components/core';
import * as d3 from 'd3';
import useDebounce from 'hooks/useDebounce';
import { clamp } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { convertNormalizedPointToPixelPoint, convertPixelPointToNormalizedPoint } from 'utils/RegionUtils';

type Point = [number, number];
type Coords = Point[];

type HotspotProps = {
  url: string;
  onChange?: (coords: Coords) => void;
  initialCoords?: number[][];
};

type PointData = {
  index: number;
};

const convertNormalizedCoordsToPixelCoords = (coords: Coords, imageWidth: number, imageHeight: number): Coords => {
  const pixelPoints = coords.map((coord) =>
    convertNormalizedPointToPixelPoint({ x: coord[0], y: coord[1] }, imageWidth, imageHeight)
  );
  return pixelPoints.map((point) => [point.x, point.y] as Point);
};

const convertPixelCoordsToNormalizedCoords = (coords: Coords, imageWidth: number, imageHeight: number): Coords => {
  return coords.map((coord) => {
    const normalizedPoint = convertPixelPointToNormalizedPoint({ x: coord[0], y: coord[1] }, imageWidth, imageHeight);
    return [normalizedPoint.x, normalizedPoint.y] as Point;
  });
};

const addPolygon = (svg, coords, width, height, completePath) => {
  const pathString =
    convertNormalizedCoordsToPixelCoords(coords, width, height)
      .map((point, i) => {
        const command = i === 0 ? 'M' : 'L';
        return `${command}${point[0]},${point[1]}`;
      })
      .join('') + (completePath ? 'Z' : '');

  svg.selectAll('path').remove();
  svg
    .append('path')
    .attr('d', pathString)
    .attr('stroke', 'rgba(0, 100, 255, 0.8)')
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', '4,4')
    .attr('fill', completePath ? 'rgba(0, 100, 255, 0.2)' : 'rgba(0, 100, 255, 0.0)')
    .style('pointer-events', completePath ? 'all' : 'none')
    .attr('cursor', completePath ? 'move' : 'default');
};

const generateCirclePoints = (startX: number, startY: number, endX: number, endY: number): Coords => {
  const points: Coords = [];

  const centerX = (startX + endX) / 2;
  const centerY = (startY + endY) / 2;
  const radiusX = Math.abs(endX - startX) / 2;
  const radiusY = Math.abs(endY - startY) / 2;

  for (let i = 0; i < 12; i++) {
    const angle = (i / 12) * 2 * Math.PI;
    const x = centerX + radiusX * Math.cos(angle);
    const y = centerY + radiusY * Math.sin(angle);
    points.push([Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))] as Point);
  }
  return points;
};

const generateSquarePoints = (startX: number, startY: number, endX: number, endY: number): Coords => {
  return [[startX, startY] as Point, [endX, startY] as Point, [endX, endY] as Point, [startX, endY] as Point];
};

export default function Hotspot({ url, onChange, initialCoords = [] }: HotspotProps) {
  const [drawType, setDrawType] = useState<'draw' | 'circle' | 'square' | null>(null);
  const [completeShape, setCompleteShape] = useState(initialCoords.length !== 0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);

  const completePath = drawType !== 'draw' || completeShape;

  const [coords, setCoords] = useState<Coords>(initialCoords as Coords);
  const debouncedCoords = useDebounce(coords, 1000);
  useEffect(() => {
    if (onChange) {
      onChange(debouncedCoords);
    }
  }, [debouncedCoords]);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || !svgRef.current) {
      return;
    }

    const container = containerRef.current;
    const svg = d3.select(svgRef.current);

    svg.selectAll('*').remove();

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const pixelCoords = convertNormalizedCoordsToPixelCoords(coords, containerWidth, containerHeight);

    // Configure D3 selection behavior
    d3.select('body').on('mousedown.d3-selection-multi', null);

    // Immediate initialization
    if (coords.length > 0) {
      addPolygon(svg, coords, containerWidth, containerHeight, completePath);
    }

    const handleResize = () => {
      // Skip resizing if we're in the middle of a drag operation
      if (isDraggingRef.current) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      if (coords.length > 0) {
        addPolygon(svg, coords, width, height, completePath);
      }

      const pixelCoords = convertNormalizedCoordsToPixelCoords(coords, width, height);
      svg.selectAll<SVGCircleElement, PointData>('circle').each(function (d) {
        if (d && pixelCoords[d.index]) {
          const [x, y] = pixelCoords[d.index];
          d3.select(this).attr('cx', x).attr('cy', y);
        }
      });
    };

    // Point drag
    const pointDrag = d3
      .drag<SVGCircleElement, PointData>()
      .on('start', () => {
        isDraggingRef.current = true;
      })
      .on('drag', (event, d) => {
        if (completeShape) {
          const index = d.index;

          const newCoords = [...coords];
          newCoords[index] = convertPixelCoordsToNormalizedCoords(
            [[event.x, event.y]],
            containerWidth,
            containerHeight
          )[0];

          setCoords(newCoords);

          d3.select(event.sourceEvent.target).attr('cx', event.x).attr('cy', event.y);

          addPolygon(svg, newCoords, containerWidth, containerHeight, completePath);
        }
      })
      .on('end', () => {
        isDraggingRef.current = false;
      });

    // Shape drag
    const shapeDrag = d3
      .drag<SVGPathElement, unknown>()
      .on('start', (event) => {
        isDraggingRef.current = true;
      })
      .on('drag', (event) => {
        if (completePath && coords.length > 0) {
          const dx = event.x - event.subject.x;
          const dy = event.y - event.subject.y;

          const newPixelCoords = pixelCoords.map((point) => [point[0] + dx, point[1] + dy] as Point);
          const newCoords = convertPixelCoordsToNormalizedCoords(newPixelCoords, containerWidth, containerHeight);

          addPolygon(svg, newCoords, containerWidth, containerHeight, completePath);

          svg.selectAll<SVGCircleElement, PointData>('circle').each(function (d) {
            if (d && newPixelCoords[d.index]) {
              const [x, y] = newPixelCoords[d.index];
              d3.select(this).attr('cx', x).attr('cy', y);
            }
          });
        }
      })
      .on('end', (event) => {
        isDraggingRef.current = false;
        if (completePath && coords.length > 0) {
          const dx = event.x - event.subject.x;
          const dy = event.y - event.subject.y;

          const newPixelCoords = pixelCoords.map((point) => [point[0] + dx, point[1] + dy] as Point);
          const newCoords = convertPixelCoordsToNormalizedCoords(newPixelCoords, containerWidth, containerHeight);

          setCoords(newCoords);

          if (onChange) {
            onChange(newCoords);
          }
        }
      });

    if (completePath) {
      svg.select('path').call(shapeDrag as any);
    }

    convertNormalizedCoordsToPixelCoords(coords, containerWidth, containerHeight).forEach((point, i) => {
      svg
        .append('circle')
        .datum({ index: i } as PointData)
        .attr('cx', point[0])
        .attr('cy', point[1])
        .attr('r', 4)
        .attr('fill', 'rgba(255, 255, 255, 0.6)')
        .attr('stroke', 'rgba(0, 0, 0, 1)')
        .attr('stroke-width', 1)
        .attr('cursor', 'move')
        .call(pointDrag as any);
    });

    // Add a single window resize listener
    window.addEventListener('resize', handleResize);

    // Handler for image load
    const img = container.querySelector('img');
    if (img) {
      img.addEventListener('load', handleResize);
    }

    // Drawing functionality
    if (drawType && !completeShape) {
      const handleSquareDrawing = () => {
        svg
          .on('mousedown', (event) => {
            if (drawType === 'square' && !completeShape) {
              setIsDrawing(true);
              const [normalizedX, normalizedY] = convertPixelCoordsToNormalizedCoords(
                [d3.pointer(event)],
                containerWidth,
                containerHeight
              )[0];

              setStartPoint([normalizedX, normalizedY]);

              const initialSquare = generateSquarePoints(normalizedX, normalizedY, normalizedX, normalizedY);
              setCoords(initialSquare);
              addPolygon(svg, initialSquare, containerWidth, containerHeight, completePath);
            }
          })
          .on('mousemove', (event) => {
            if (drawType === 'square' && isDrawing && startPoint) {
              const [normalizedX, normalizedY] = convertPixelCoordsToNormalizedCoords(
                [d3.pointer(event)],
                containerWidth,
                containerHeight
              )[0];

              const updatedSquare = generateSquarePoints(startPoint[0], startPoint[1], normalizedX, normalizedY);
              setCoords(updatedSquare);
              addPolygon(svg, updatedSquare, containerWidth, containerHeight, completePath);
            }
          })
          .on('mouseup', () => {
            if (drawType === 'square' && isDrawing) {
              setIsDrawing(false);
              setCompleteShape(true);
              if (onChange) {
                onChange(coords);
              }
            }
          });
      };

      const handleCircleDrawing = () => {
        svg
          .on('mousedown', (event) => {
            if (drawType === 'circle' && !completeShape) {
              setIsDrawing(true);
              const [normalizedX, normalizedY] = convertPixelCoordsToNormalizedCoords(
                [d3.pointer(event)],
                containerWidth,
                containerHeight
              )[0];

              setStartPoint([normalizedX, normalizedY]);

              const initialCircle = generateCirclePoints(normalizedX, normalizedY, normalizedX, normalizedY);
              setCoords(initialCircle);
              addPolygon(svg, initialCircle, containerWidth, containerHeight, completePath);
            }
          })
          .on('mousemove', (event) => {
            if (drawType === 'circle' && isDrawing && startPoint) {
              const [normalizedX, normalizedY] = convertPixelCoordsToNormalizedCoords(
                [d3.pointer(event)],
                containerWidth,
                containerHeight
              )[0];

              const updatedCircle = generateCirclePoints(startPoint[0], startPoint[1], normalizedX, normalizedY);
              setCoords(updatedCircle);
              addPolygon(svg, updatedCircle, containerWidth, containerHeight, completePath);
            }
          })
          .on('mouseup', () => {
            if (drawType === 'circle' && isDrawing) {
              setIsDrawing(false);
              setCompleteShape(true);
              if (onChange) {
                onChange(coords);
              }
            }
          });
      };

      const handleFreeDrawing = () => {
        svg.on('click', (event) => {
          if (drawType === 'draw' && !completeShape) {
            const [normalizedX, normalizedY] = convertPixelCoordsToNormalizedCoords(
              [d3.pointer(event)],
              containerWidth,
              containerHeight
            )[0];

            if (coords.length > 2) {
              const firstPoint = coords[0];
              const dx = Math.abs(normalizedX - firstPoint[0]);
              const dy = Math.abs(normalizedY - firstPoint[1]);

              if (dx < 0.01 && dy < 0.01) {
                setCompleteShape(true);
                if (onChange) {
                  onChange(coords);
                }
                return;
              }
            }

            const newCoords = [...coords, [normalizedX, normalizedY] as Point];
            setCoords(newCoords);

            if (newCoords.length > 1) {
              addPolygon(svg, newCoords, containerWidth, containerHeight, completePath);
            }
          }
        });
      };

      if (drawType === 'square') {
        handleSquareDrawing();
      } else if (drawType === 'circle') {
        handleCircleDrawing();
      } else if (drawType === 'draw') {
        handleFreeDrawing();
      }
    }

    // Force a redraw on the next tick to ensure handlers are attached
    setTimeout(() => {
      // Re-create all interactive elements
      svg.selectAll('*').remove();

      if (coords.length > 0) {
        addPolygon(svg, coords, containerWidth, containerHeight, completePath);
      }

      if (completePath) {
        svg.select('path').call(shapeDrag as any);
      }

      convertNormalizedCoordsToPixelCoords(coords, containerWidth, containerHeight).forEach((coord, i) => {
        svg
          .append('circle')
          .datum({ index: i } as PointData)
          .attr('cx', coord[0])
          .attr('cy', coord[1])
          .attr('r', 4)
          .attr('fill', 'rgba(255, 255, 255, 0.6)')
          .attr('stroke', 'rgba(0, 0, 0, 1)')
          .attr('stroke-width', 1)
          .attr('cursor', 'move')
          .call(pointDrag as any);
      });
    }, 0);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (img) {
        img.removeEventListener('load', handleResize);
      }
      svg.on('mousedown', null).on('mousemove', null).on('mouseup', null).on('click', null);
    };
  }, [coords, completePath, drawType, isDrawing, startPoint, url]);

  useEffect(() => {
    if (drawType !== null && !completeShape) {
      setCoords([]);
      setStartPoint(null);
      setIsDrawing(false);
    }
  }, [drawType]);

  const handleReset = () => {
    setCoords([]);
    setCompleteShape(false);
    setIsDrawing(false);
    setStartPoint(null);
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        <img
          src={url}
          alt="Region Image"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'block',
            width: '100%',
          }}
        />
        <svg
          ref={svgRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      </div>
      <MemreFlexBox direction="row" gap={1} justify="flex-start" align="center" sx={{ width: '100%', pt: 0.5 }}>
        {!completeShape ? (
          <MemreToggleButtonGroup
            options={[
              { label: 'Draw', value: 'draw', iconName: 'edit' },
              { label: 'Ellipse', value: 'circle', iconName: 'circle' },
              { label: 'Rectangle', value: 'square', iconName: 'square' },
            ]}
            value={drawType}
            onChange={setDrawType}
          />
        ) : (
          <MemreToggleButtonGroup
            options={[{ label: 'Reset', value: 'reset', iconName: 'refresh' }]}
            value="reset"
            onChange={() => handleReset()}
          />
        )}
      </MemreFlexBox>
    </>
  );
}
